import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaUser, FaTimes, FaKey } from "react-icons/fa";
import Swal from "sweetalert2";
import useAxios from "../utils/useAxios";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from '../context/AuthContext';
import { useNavigate } from "react-router-dom";

const SuperAdminEmployePage = () => {
  const api = useAxios();
  const { sidebarVisible } = useSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [employes, setEmployes] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    telephone: "",
    poste: "",
    matricule: "",
    organisation: "",
    organisations_multi: [],
    password: "",
    password2: "",
  });

  const fetchEmployes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/employes/");
      setEmployes(data);
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Erreur",
        text: "Impossible de charger les employés.",
        icon: "error",
        toast: true,
        position: "top-right",
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganisations = async () => {
    try {
      const { data } = await api.get("/organisations/");
      setOrganisations(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEmployes();
    fetchOrganisations();
  }, []);

  const openModal = (employe = null) => {
    if (employe) {
      setIsEditing(true);
      setSelectedEmploye(employe.id);
      setFormData({
        username: employe.username,
        email: employe.email,
        first_name: employe.first_name,
        last_name: employe.last_name,
        telephone: employe.telephone,
        poste: employe.poste,
        matricule: employe.matricule,
        organisation: employe.organisation_id || "",
        organisations_multi: employe.organisations_multi_ids || [],
        password: "",
        password2: "",
      });
    } else {
      setIsEditing(false);
      setSelectedEmploye(null);
      setFormData({
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        telephone: "",
        poste: "",
        matricule: "",
        organisation: "",
        organisations_multi: [],
        password: "",
        password2: "",
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.password2) {
      Swal.fire({
        title: "Erreur",
        text: "Les mots de passe ne correspondent pas.",
        icon: "error",
        toast: true,
        position: "top-right",
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      return;
    }
    try {
      if (isEditing && selectedEmploye) {
        await api.put(`/superadmin/employes/${selectedEmploye}/update/`, formData);
        Swal.fire({
          title: "Employé mis à jour",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        await api.post("/superadmin/employes/create/", formData);
        Swal.fire({
          title: "Employé créé",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      fetchEmployes();
      closeModal();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Erreur",
        text: "Impossible d'enregistrer l'employé.",
        icon: "error",
        toast: true,
        position: "top-right",
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Supprimer cet employé ?",
      text: "Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, supprimer",
    });
    if (confirm.isConfirmed) {
      try {
        await api.delete(`/superadmin/employes/${id}/`);
        setEmployes((prev) => prev.filter((emp) => emp.id !== id));
        Swal.fire({
          title: "Supprimé !",
          text: "L'employé a été supprimé.",
          icon: "success",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        Swal.fire({
          title: "Erreur",
          text: "Impossible de supprimer l'employé.",
          icon: "error",
          toast: true,
          position: "top-right",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    }
  };


  
  const styles = {
    container: { marginLeft: sidebarVisible ? 250 : 0, padding: 20, minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 25 },
    title: { fontSize: 28, fontWeight: 700, color: "#1B019B", display: "flex", alignItems: "center", gap: 12 },
    addButton: { background: "#1B019B", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 10, transition: "0.3s" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 20 },
    card: { background: "#fff", padding: 20, borderRadius: 16, boxShadow: "0 8px 20px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", justifyContent: "space-between", transition: "0.3s", cursor: "pointer" },
    cardTitle: { fontSize: 20, fontWeight: 600, color: "#1B019B", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
    cardText: { color: "#555", marginBottom: 8, fontSize: 14 },
    cardActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 15 },
    editBtn: { background: "#ffc107", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", fontWeight: 600 },
    deleteBtn: { background: "#dc3545", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", fontWeight: 600 },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { background: "#fff", borderRadius: 16, padding: 25, maxWidth: 700, width: "90%", position: "relative", maxHeight: "90vh", overflowY: "auto" },
    input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ccc", marginBottom: 15, fontSize: 14 },
    submitBtn: { background: "#1B019B", color: "#fff", border: "none", padding: 12, borderRadius: 8, width: "100%", cursor: "pointer", fontWeight: 600, fontSize: 16 },
    closeBtn: { position: "absolute", top: 15, right: 15, background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#555" },
    noData: { textAlign: "center", color: "#555", marginTop: 50, fontSize: 16 },
    permissionsButton: { background: "#6f42c1", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", fontWeight: 600, marginLeft: 5 }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          <FaUser /> Gestion des Administrateur des organisations
        </h3>
        <button
          style={styles.addButton}
          onClick={() => openModal()}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#2a0fdb")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1B019B")}
        >
          <FaPlus /> Ajouter maintenant l'administrateur
        </button>
      </div>

      {loading ? (
        <p style={styles.noData}>Chargement...</p>
      ) : employes.length > 0 ? (
        <div style={styles.grid}>
          {employes.map((emp) => (
            <div key={emp.id} style={styles.card}>
              <div>
                <h4 style={styles.cardTitle}>
                  {emp.first_name} {emp.last_name}
                </h4>
                <p style={styles.cardText}>Matricule: {emp.matricule}</p>
                <p style={styles.cardText}>
                  Organisation principale: {emp.organisation || "—"}
                </p>
                {emp.organisations_multi &&
                  emp.organisations_multi.length > 0 && (
                    <p style={styles.cardText}>
                      Organisations supplémentaires:{" "}
                      {emp.organisations_multi.join(", ")}
                    </p>
                  )}
              </div>
              <div style={styles.cardActions}>
                <button style={styles.editBtn} onClick={() => openModal(emp)}>
                  <FaEdit />
                </button>
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(emp.id)}
                >
                  <FaTrash />
                </button>
                {(user?.is_super_admin ||
                  user?.permissions?.includes("VIEW_PERMISSIONS")) && (
                  <button
                    style={styles.permissionsButton}
                    onClick={() => navigate(`/permissions/employe/${emp.id}`)}
                  >
                    <FaKey />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={styles.noData}>Aucun employé trouvé.</p>
      )}

      {/* Modal */}
      {modalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button onClick={closeModal} style={styles.closeBtn}>
              <FaTimes />
            </button>
            <h4
              style={{
                textAlign: "center",
                marginBottom: 20,
                color: "#1B019B",
              }}
            >
              {isEditing ? "Modifier Employé" : "Nouvel Employé"}
            </h4>
            <form
              onSubmit={handleSubmit}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 15,
              }}
            >
              <input
                type="text"
                placeholder="Nom d'utilisateur"
                style={styles.input}
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
              />
              <input
                type="email"
                placeholder="Email"
                style={styles.input}
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
              <input
                type="text"
                placeholder="Prénom"
                style={styles.input}
                value={formData.first_name}
                onChange={(e) =>
                  setFormData({ ...formData, first_name: e.target.value })
                }
                required
              />
              <input
                type="text"
                placeholder="Nom"
                style={styles.input}
                value={formData.last_name}
                onChange={(e) =>
                  setFormData({ ...formData, last_name: e.target.value })
                }
                required
              />
              <input
                type="text"
                placeholder="Téléphone"
                style={styles.input}
                value={formData.telephone}
                onChange={(e) =>
                  setFormData({ ...formData, telephone: e.target.value })
                }
                required
              />
              <input
                type="text"
                placeholder="Poste"
                style={styles.input}
                value={formData.poste}
                onChange={(e) =>
                  setFormData({ ...formData, poste: e.target.value })
                }
                required
              />
              <input
                type="text"
                placeholder="Matricule"
                style={styles.input}
                value={formData.matricule}
                onChange={(e) =>
                  setFormData({ ...formData, matricule: e.target.value })
                }
                required
              />

              <select
                style={styles.input}
                value={formData.organisation}
                onChange={(e) =>
                  setFormData({ ...formData, organisation: e.target.value })
                }
              >
                <option value="">-- Organisation principale --</option>
                {organisations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.nom}
                  </option>
                ))}
              </select>

              <div
                style={{
                  gridColumn: "1 / -1",
                  maxHeight: 150,
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <label
                  style={{
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 5,
                  }}
                >
                  Organisations supplémentaires :
                </label>
                {organisations.map((org) => (
                  <div key={org.id} style={{ marginBottom: 5 }}>
                    <input
                      type="checkbox"
                      id={`org-${org.id}`}
                      checked={formData.organisations_multi.includes(org.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            organisations_multi: [
                              ...formData.organisations_multi,
                              org.id,
                            ],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            organisations_multi:
                              formData.organisations_multi.filter(
                                (id) => id !== org.id
                              ),
                          });
                        }
                      }}
                    />
                    <label htmlFor={`org-${org.id}`} style={{ marginLeft: 8 }}>
                      {org.nom}
                    </label>
                  </div>
                ))}
              </div>

              <input
                type="password"
                placeholder="Mot de passe"
                style={styles.input}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required={!isEditing}
              />
              <input
                type="password"
                placeholder="Confirmer mot de passe"
                style={styles.input}
                value={formData.password2}
                onChange={(e) =>
                  setFormData({ ...formData, password2: e.target.value })
                }
                required={!isEditing}
              />

              <button
                type="submit"
                style={{ ...styles.submitBtn, gridColumn: "1 / -1" }}
              >
                {isEditing ? "Mettre à jour" : "Créer"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminEmployePage;
